import Axios from 'axios';

export interface CostBillingBreakdown {
  cost: number;
  billing: number;
}

export interface ConsumableBreakdownItem extends CostBillingBreakdown {
  name: string;
  quantity: number;
  measurementUnit: string;
}

export interface EquipmentBreakdownItem extends CostBillingBreakdown {
  name: string;
  isCapping?: boolean;
  cappedDaysCount: number;
  uncappedDaysCount: number;
  equipmentCount: number;
  equipmentDetails?: {
    equipmentName: string;
    installedDate: string;
    pickedUpDate: string;
    cappedDaysCount: number;
    uncappedDaysCount: number;
  }[];
}

export interface AwayFromHomeSeries {
  startDate: string;
  endDate: string;
  nights: number;
}

export interface AwayFromHomeBreakdown {
  totalNights: number;
  nightlyRate: number;
  totalCost: number;
  nightlyBillingRate: number;
  totalBilling: number;
  series: AwayFromHomeSeries[];
}

export interface AwayFromHomeTechnicianBreakdown {
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
  totalNights: number;
  nightlyRate: number;
  totalCost: number;
  nightlyBillingRate: number;
  totalBilling: number;
  series: AwayFromHomeSeries[];
}

export interface LabourTechnicianBreakdown extends CostBillingBreakdown {
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
  totalMinutes: number;
  awayFromHome: AwayFromHomeBreakdown;
  breakdown: {
    categoryName: string;
    minutes: number;
    cost: number;
    billing: number;
  }[];
}

export interface OtherExpenseBreakdownItem extends CostBillingBreakdown {
  type: string;
  note?: string;
}

export interface JobCostBreakdown {
  consumables: {
    totalCost: number;
    totalBilling: number;
    items: ConsumableBreakdownItem[];
  };
  equipment: {
    totalCost: number;
    totalBilling: number;
    categories: EquipmentBreakdownItem[];
  };
  labour: {
    totalCost: number;
    totalBilling: number;
    totalAwayFromHomeCost: number;
    totalAwayFromHomeBilling: number;
    technicians: LabourTechnicianBreakdown[];
  };
  otherExpenses: {
    totalCost: number;
    totalBilling: number;
    items: OtherExpenseBreakdownItem[];
  };
  summary: JobCostSummary;
}

export type DifferenceDirection = 'up' | 'down' | 'equal';

/**
 * The costing summary worked out against the hand-entered actual billed
 * amount, and how it compares with the estimate. Computed by the API; amounts
 * are signed (actual minus estimate).
 */
export interface ActualBillingSummary {
  billedAmount: number;
  grossProfit: number;
  /** Null when the actual billed amount is 0 */
  marginPercentage: number | null;
  difference: {
    billing: {
      amount: number;
      /** Null when the estimated billing is 0 */
      percentageOfEstimate: number | null;
      direction: DifferenceDirection;
    };
    grossProfit: { amount: number; direction: DifferenceDirection };
    margin: {
      /** Percentage points; null when either margin is not calculable */
      points: number | null;
      direction: DifferenceDirection | null;
    };
  };
}

/** Per-site-visit totals. `totalJobBilling` etc. are the estimate */
export interface CostSummary {
  totalJobCost: number;
  totalJobBilling: number;
  grossProfit: number;
  marginPercentage: number;
}

/** Job-level totals: the estimate, plus the actual once one is entered */
export interface JobCostSummary extends CostSummary {
  actual?: ActualBillingSummary | null;
}

export interface InspectionCostBreakdown {
  inspectionId: string;
  inspectionDate: string;
  isInitialAttendance: boolean;
  inspectionIndex: number;
  consumables: JobCostBreakdown['consumables'];
  labour: JobCostBreakdown['labour'];
  otherExpenses: JobCostBreakdown['otherExpenses'];
  summary: CostSummary;
}

export interface JobCostBreakdownByInspection {
  inspections: InspectionCostBreakdown[];
  equipment: JobCostBreakdown['equipment'];
  awayFromHome: {
    totalCost: number;
    totalBilling: number;
    technicians: AwayFromHomeTechnicianBreakdown[];
  };
  summary: JobCostBreakdown['summary'];
}

export async function getJobCostBreakdown(
  jobId: string
): Promise<JobCostBreakdown> {
  const res = await Axios.get(`/api/v1/job-cost/${jobId}/cost-breakdown`);
  return res.data;
}

export async function getJobCostBreakdownByInspection(
  jobId: string
): Promise<JobCostBreakdownByInspection> {
  const res = await Axios.get(
    `/api/v1/job-cost/${jobId}/cost-breakdown-by-inspection`
  );
  return res.data;
}

/** Excl. GST. Pass `null` to remove the actual billed amount */
export async function setActualBilledAmount(
  jobId: string,
  actualBilledAmount: number | null
): Promise<{ actualBilledAmount: number | null }> {
  const res = await Axios.put(
    `/api/v1/job-cost/${jobId}/actual-billed-amount`,
    { actualBilledAmount }
  );
  return res.data;
}

export async function recalculateJobRates(
  jobId: string,
  date: string
): Promise<{ consumablesUpdated: number; shiftsUpdated: number }> {
  const res = await Axios.post(`/api/v1/job-cost/${jobId}/recalculate-rates`, {
    date,
  });
  return res.data;
}
